import React from 'react';
import { Row, Col, Card, Statistic, Progress, Space, Select, Spin, Alert } from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DashboardOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  TeamOutlined,
  DeleteOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import { Area } from '@ant-design/charts';
import { useKPIMetrics } from '../../../hooks/useManufacturingDashboard';

interface KPIWidgetsGridProps {
  period: 'today' | 'week' | 'month';
  onPeriodChange: (period: 'today' | 'week' | 'month') => void;
  refreshInterval?: number;
}

const KPIWidgetsGrid: React.FC<KPIWidgetsGridProps> = ({
  period,
  onPeriodChange,
  refreshInterval,
}) => {
  const { data: kpis, isLoading, error } = useKPIMetrics(period, refreshInterval);

  if (error) {
    return (
      <Alert
        message="Error Loading KPI Metrics"
        description={error.message}
        type="error"
        showIcon
        style={{ marginBottom: 24 }}
      />
    );
  }

  if (isLoading || !kpis) {
    return (
      <Card style={{ marginBottom: 24 }}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  // Helper function to get color based on threshold
  const getOEEColor = (value: number) => {
    if (value >= 85) return '#52c41a';
    if (value >= 70) return '#faad14';
    return '#ff4d4f';
  };

  const getEfficiencyColor = (value: number) => {
    if (value >= 90) return '#52c41a';
    if (value >= 75) return '#faad14';
    return '#ff4d4f';
  };

  // Mock sparkline data (in real app, would come from trends)
  const generateSparklineData = (value: number) => {
    return Array.from({ length: 10 }, (_, i) => ({
      index: i,
      value: value + (Math.random() - 0.5) * 20,
    }));
  };

  // Sparkline chart configuration helper function
  const getSparklineConfig = (data: any[], color: string) => ({
    data,
    xField: 'index',
    yField: 'value',
    smooth: true,
    height: 40,
    color,
    areaStyle: {
      fillOpacity: 0.2,
    },
    line: {
      size: 2,
    },
    xAxis: false,
    yAxis: false,
    tooltip: false,
    animation: false,
  });

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Key Performance Indicators</h3>
        <Select
          value={period}
          onChange={onPeriodChange}
          style={{ width: 120 }}
          options={[
            { value: 'today', label: 'Today' },
            { value: 'week', label: 'This Week' },
            { value: 'month', label: 'This Month' },
          ]}
        />
      </div>

      <Row gutter={[16, 16]}>
        {/* OEE - Overall Equipment Effectiveness */}
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>OEE (calculated)</span>
                <DashboardOutlined style={{ fontSize: 18, color: getOEEColor(kpis.oee) }} />
              </div>
              <Statistic
                value={kpis.oee}
                precision={1}
                suffix="%"
                valueStyle={{ color: getOEEColor(kpis.oee), fontSize: 32 }}
              />
              <Progress
                percent={kpis.oee}
                strokeColor={getOEEColor(kpis.oee)}
                showInfo={false}
                size="small"
              />
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                {kpis.oeeBasis || 'Utilization × time-log efficiency × first-pass yield. Not machine-state OEE.'}
              </div>
            </Space>
          </Card>
        </Col>

        {/* First Pass Yield */}
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>First Pass Yield</span>
                <CheckCircleOutlined style={{ fontSize: 18, color: '#52c41a' }} />
              </div>
              <Statistic
                value={kpis.firstPassYield}
                precision={1}
                suffix="%"
                valueStyle={{ fontSize: 32 }}
              />
              <div style={{ height: 40 }}>
                <Area {...getSparklineConfig(generateSparklineData(kpis.firstPassYield), '#52c41a')} />
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                Quality without rework
              </div>
            </Space>
          </Card>
        </Col>

        {/* Rejection Rate */}
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>Rejection Rate</span>
                <CloseCircleOutlined style={{ fontSize: 18, color: kpis.rejectionRate > 5 ? '#ff4d4f' : '#52c41a' }} />
              </div>
              <Statistic
                value={kpis.rejectionRate}
                precision={1}
                suffix="%"
                valueStyle={{ 
                  fontSize: 32,
                  color: kpis.rejectionRate > 5 ? '#ff4d4f' : '#52c41a'
                }}
              />
              <div style={{ height: 40 }}>
                <Area 
                  {...getSparklineConfig(
                    generateSparklineData(kpis.rejectionRate), 
                    kpis.rejectionRate > 5 ? '#ff4d4f' : '#52c41a'
                  )} 
                />
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                {kpis.rejectionRate > 5 ? '⚠️ Above threshold (5%)' : '✓ Within target'}
              </div>
            </Space>
          </Card>
        </Col>

        {/* On-Time Delivery */}
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>OTIF</span>
                <ClockCircleOutlined style={{ fontSize: 18, color: '#145A54' }} />
              </div>
              <Statistic
                value={kpis.otif ?? kpis.onTimeDelivery}
                precision={1}
                suffix="%"
                valueStyle={{ fontSize: 32, color: '#145A54' }}
              />
              <Progress
                percent={kpis.otif ?? kpis.onTimeDelivery}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
                showInfo={false}
                size="small"
              />
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                {kpis.otifHitLines ?? 0} of {kpis.otifDueLines ?? 0} lines delivered in full on or before the promise date.
                Work-order completion on time is {kpis.onTimeDelivery}%.
              </div>
            </Space>
          </Card>
        </Col>

        {/* Avg Cycle Time */}
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>Avg Cycle Time</span>
                <FieldTimeOutlined style={{ fontSize: 18, color: '#722ed1' }} />
              </div>
              <Statistic
                value={kpis.avgCycleTime}
                precision={1}
                suffix="hrs"
                valueStyle={{ fontSize: 32, color: '#722ed1' }}
              />
              <div style={{ height: 40 }}>
                <Area {...getSparklineConfig(generateSparklineData(kpis.avgCycleTime), '#722ed1')} />
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                Start to completion time
              </div>
            </Space>
          </Card>
        </Col>

        {/* Capacity Utilization */}
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>Capacity Utilization</span>
                <ThunderboltOutlined style={{ fontSize: 18, color: '#faad14' }} />
              </div>
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Progress
                  type="circle"
                  percent={kpis.capacityUtilization}
                  strokeColor={{
                    '0%': '#faad14',
                    '100%': '#faad14',
                  }}
                  width={100}
                  format={(percent) => `${percent?.toFixed(1)}%`}
                />
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c', textAlign: 'center' }}>
                Resource usage efficiency
              </div>
            </Space>
          </Card>
        </Col>

        {/* Operator Efficiency */}
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>Operator Efficiency</span>
                <TeamOutlined style={{ fontSize: 18, color: getEfficiencyColor(kpis.operatorEfficiency) }} />
              </div>
              <Statistic
                value={kpis.operatorEfficiency}
                precision={1}
                suffix="%"
                valueStyle={{ 
                  fontSize: 32,
                  color: getEfficiencyColor(kpis.operatorEfficiency)
                }}
              />
              <Progress
                percent={kpis.operatorEfficiency}
                strokeColor={getEfficiencyColor(kpis.operatorEfficiency)}
                showInfo={false}
                size="small"
              />
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                {kpis.operatorEfficiency >= 90 ? '✓ Excellent' : 
                 kpis.operatorEfficiency >= 75 ? '⚠️ Good' : '❌ Needs improvement'}
              </div>
            </Space>
          </Card>
        </Col>

        {/* Scrap Rate */}
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>Scrap Rate</span>
                <DeleteOutlined style={{ fontSize: 18, color: kpis.scrapRate > 3 ? '#ff4d4f' : '#52c41a' }} />
              </div>
              <Statistic
                value={kpis.scrapRate}
                precision={1}
                suffix="%"
                valueStyle={{ 
                  fontSize: 32,
                  color: kpis.scrapRate > 3 ? '#ff4d4f' : '#52c41a'
                }}
              />
              <div style={{ height: 40 }}>
                <Area 
                  {...getSparklineConfig(
                    generateSparklineData(kpis.scrapRate), 
                    kpis.scrapRate > 3 ? '#ff4d4f' : '#52c41a'
                  )} 
                />
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                {kpis.scrapRate > 3 ? '⚠️ Above target (3%)' : '✓ Within limits'}
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default KPIWidgetsGrid;
